/**
 * SectorOverlayChip — shows the run's resolved sector overlay (auto-detected from
 * client.sector or manually overridden), the applicable regulators and the count of
 * pre-loaded RAID risks, with a dropdown to override when the free-text sector
 * doesn't match cleanly. Drives the sector-specific accelerators in every deliverable.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Building2, ShieldCheck, Pencil, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function SectorOverlayChip({ runId }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/assessments/runs/${runId}/sector-overlay`);
      setData(res.data);
    } catch {
      setData(null);
    }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  const save = async (overlayId) => {
    setSaving(true);
    try {
      await api.put(`/admin/assessments/runs/${runId}/sector-overlay`, { overlay_id: overlayId });
      await load();
      setEditing(false);
      toast.success("Sector overlay updated — deliverables will use the new regulators & RAID");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update overlay");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;

  const mismatch = !data.matched && !data.overridden;

  return (
    <div className={`ek-card p-4 ${mismatch ? "border-amber-300 bg-amber-50/40" : ""}`} data-testid="sector-overlay-chip">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-[#1A1A1F]">
          <Building2 size={14} className="text-[#7737F6]" />
          Sector playbook
        </div>
        <button
          type="button"
          onClick={() => setEditing((v) => !v)}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7737F6] hover:underline"
          data-testid="sector-overlay-edit-btn"
        >
          <Pencil size={11} /> {editing ? "Cancel" : "Override"}
        </button>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-[#EFE7FE] text-[#5B2BD6] text-[12px] font-semibold px-2.5 py-1" data-testid="sector-overlay-name">
          {data.display}
        </span>
        <span className="text-[11px] text-[#777783]">
          {data.overridden ? "manually set" : data.matched ? "auto-detected" : `no match for "${data.client_sector || "—"}" → generic`}
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] text-[#1E7A4D] font-semibold">
          <ShieldCheck size={11} /> {data.risk_count} RAID risks pre-loaded
        </span>
      </div>

      <p className="mt-2 text-[11px] leading-relaxed text-[#555560]">
        <strong className="text-[#1A1A1F]">Regulators / standards:</strong> {[...data.regulators, ...data.standards].join(" · ")}
      </p>

      {mismatch && !editing && (
        <div className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-100/70 text-amber-900 text-[11px] px-2.5 py-1.5" data-testid="sector-overlay-mismatch">
          <AlertTriangle size={12} className="shrink-0 mt-0.5" />
          <span>No sector overlay matched <strong>"{data.client_sector || "—"}"</strong> — the pack will ship on the generic playbook. Click <strong>Override</strong> to pick the right sector.</span>
        </div>
      )}

      {editing && (
        <div className="mt-3 flex items-center gap-2">
          <select
            defaultValue={data.overridden ? data.id : "auto"}
            disabled={saving}
            onChange={(e) => save(e.target.value)}
            className="ek-input text-[13px] flex-1"
            data-testid="sector-overlay-select"
          >
            <option value="auto">Auto-detect from client sector</option>
            {(data.available || []).map((o) => (
              <option key={o.id} value={o.id}>{o.display}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
